/**
 * HeavyTabGate — wraps the heavy data tabs (Dashboard / Nodes / Pulse Groups).
 *
 * When the runtime "Enable Tabs" flag is OFF, it renders the friendly
 * TabsDisabledState instead of the children. Because the (lazy) page element
 * is NOT rendered in that case, its chunk never loads, it never mounts, and
 * none of its data-fetching effects / polling fire — exactly the goal of the
 * gate (no wasted backend load before the cluster is started).
 *
 * Sidebar Roles + Enable-Tabs Gate (2026-06-07).
 */
import { type ReactNode } from 'react';
import { Spinner } from '@/components/common';
import { useTabsEnabled } from '@/hooks/useTabsEnabled';
import TabsDisabledState from './TabsDisabledState';

interface HeavyTabGateProps {
  /** Tab name for the placeholder copy, e.g. "Dashboard". */
  label?: string;
  children: ReactNode;
}

export default function HeavyTabGate({ label, children }: HeavyTabGateProps) {
  const { tabsEnabled, loading, refetch } = useTabsEnabled();

  // First load not yet resolved — show a spinner rather than flashing the
  // placeholder (avoids a flicker when tabs are actually enabled).
  if (loading && tabsEnabled === null) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!tabsEnabled) {
    return <TabsDisabledState pageLabel={label} onEnabled={refetch} />;
  }

  return <>{children}</>;
}
